import { faFileSpreadsheet, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AlertModal,
  Box,
  ContextMenu,
  ContextMenuItem,
  ContextMenuList,
  ContextMenuTrigger,
  ErrorModal,
  Flex,
  Heading,
  PlusIconLegacy,
  Text,
  TextButton,
  Toast,
} from '@modusign/design-system';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router';

import noop from '@libs/noop';

import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import Overlay from '@app/components/Overlay';
import { ADDRESS_BOOK_ERROR_MESSAGE } from '@app/features/addressBook/constants';
import useContacts from '@app/features/addressBook/hooks/useContacts';
import useCreateContact from '@app/features/addressBook/hooks/useCreateContact';
import useSearchFilterContext from '@app/features/addressBook/hooks/useSearchFilterContext';
import useResponsiveValue from '@app/hooks/useResponsiveValue';

import NewContactModal from '../NewContactModal';

export default function SettingsAddressBookPageHeader() {
  const history = useHistory();

  const isMobileWidth = useResponsiveValue([true, true, false]);

  const [isScreenWidthAlertOpen, setIsScreenWidthAlertOpen] = useState(false);
  const [isNewContactModalOpen, setIsNewContactModalOpen] = useState(false);
  const { limit, page, searchKeyword, setDisplayKeyword, setPage } = useSearchFilterContext();

  const { refetch } = useContacts({
    limit,
    offset: (page - 1) * limit,
    name: searchKeyword || undefined,
    companyName: searchKeyword || undefined,
  });

  const resetContactsList = useCallback(() => {
    if (page !== 1) {
      setPage(1);
    }

    if (searchKeyword) {
      setDisplayKeyword('');
    }

    if (page === 1 && !searchKeyword) {
      refetch();
    }
  }, [page, refetch, searchKeyword, setDisplayKeyword, setPage]);

  const {
    error: createContactError,
    isLoading: isCreateContactLoading,
    mutate: createContact,
    reset: resetCreateContact,
  } = useCreateContact({
    onSuccess: () => {
      Toast.success({ description: '연락처를 추가했습니다.' });
      resetContactsList();
    },
  });

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between" mb={3}>
        <Heading fontSize="md">주소록</Heading>
        <ContextMenu isOpen onOpenChange={noop}>
          <Box
            position="relative"
            sx={{
              '& .SettingsAddress__NewAddress-button': {
                transition: '0.3s visibility',
              },
              '&:hover': {
                '& .SettingsAddress__NewAddress-button': {
                  visibility: 'visible',
                },
              },
            }}
          >
            <ContextMenuTrigger>
              <TextButton
                onClick={() => {
                  if (isMobileWidth) {
                    setIsScreenWidthAlertOpen(true);
                  }
                }}
              >
                <PlusIconLegacy mr="2px" />새 연락처
              </TextButton>
            </ContextMenuTrigger>
            <ContextMenuList
              align="right"
              className="SettingsAddress__NewAddress-button"
              display={isMobileWidth ? 'none' : 'block'}
              minWidth="160px"
              right="-125px"
              sx={{ visibility: 'hidden' }}
              top={6}
            >
              <ContextMenuItem onClick={() => setIsNewContactModalOpen(true)}>
                <Box as="span" mr={2}>
                  <FontAwesomeIcon icon={faUser} />
                </Box>
                한 개씩 추가
              </ContextMenuItem>
              <ContextMenuItem
                onClick={() => {
                  history.push('/settings/address-book/excel-upload');
                }}
              >
                <Box as="span" mr={2}>
                  <FontAwesomeIcon icon={faFileSpreadsheet} />
                </Box>
                여러 개 일괄 추가
              </ContextMenuItem>
            </ContextMenuList>
          </Box>
        </ContextMenu>
      </Flex>
      <Text as="p" color="textNeutralLight" mb={7}>
        자주 이용하는 연락처를 추가하여, 서명자나 참조자 정보를 편리하게 입력해 보세요.
      </Text>
      <NewContactModal
        isOpen={isNewContactModalOpen}
        onClose={() => setIsNewContactModalOpen(false)}
        onSubmit={(contactValue) => {
          setIsNewContactModalOpen(false);
          createContact(contactValue);
        }}
      />
      <ErrorModal
        error={createContactError}
        errorMessageMapper={ADDRESS_BOOK_ERROR_MESSAGE}
        isOpen
        title="연락처 추가 실패"
        onClose={() => {
          resetCreateContact();
        }}
      />
      <AlertModal
        isOpen={isScreenWidthAlertOpen}
        maxWidth="xs"
        title="이용 안내"
        variant="warning"
        onClose={() => setIsScreenWidthAlertOpen(false)}
      >
        접속한 브라우저의 화면 크기가 너무 작습니다.
        <br />
        PC와 같이 더 큰 화면에서 이용해 주세요.
      </AlertModal>
      {isCreateContactLoading && (
        <Overlay>
          <GlobalLoadingSpinner />
        </Overlay>
      )}
    </>
  );
}
